/**
 * PostHog's setup, shared by the booking app (src/main.tsx) and the
 * membership-first app (apps/membership-first/src/main.tsx). Both apps start
 * PostHog from here so the ingest proxy, the consent handling, the replay
 * masking and the shared visitor id cannot drift apart between them
 * (AHD-14135: membership-first shipped without any of it, and sent nothing).
 */
import type { PostHog, PostHogConfig } from "posthog-js";
import { identifyFromUrlPh } from "@ahead-health/core/analytics/identifyFromUrlPh";
import { isTestMode } from "@ahead-health/core/system-checks/testMode";
import {
  applyInitialPosthogConsent,
  consentAwarePosthogPersistence,
} from "@ahead-health/core/ui/cookieConsent";

export const posthogKey: string | undefined = import.meta.env
  .VITE_PUBLIC_POSTHOG_KEY;

// Ahead's own PostHog ingestion endpoint (AHD-4597). Ad blockers and tracker
// blocklists carry *.i.posthog.com, so talking to PostHog directly loses a
// double-digit share of captures, feature-flag evaluations and session
// replays, silently: the events never arrive, so nothing in the dashboards
// says they are missing. A first-party Ahead domain is not on those lists.
//
// The proxy itself is owned and configured by the report app
// (ahead-mono packages/next-app/next.config.ts: /ingest/* to eu.i.posthog.com,
// /ingest/static/* to the EU asset host, skipTrailingSlashRedirect). Booking
// is a consumer of it and must NOT grow a copy of those rewrites: two repos
// defining the same route is how they drift. Changing this path means
// changing it in the report app first.
//
// Unlike the report app, which reaches the proxy same-origin as "/ingest",
// booking is cross-origin against my.aheadhealth.com, so this depends on
// PostHog's CORS headers passing back through the proxy. Verified from the
// booking origin: POST /ingest/e/ returns 200 with
// access-control-allow-origin: https://booking.aheadhealth.com, and the
// OPTIONS preflight returns 204 allowing GET, POST, OPTIONS. The same
// preflight from https://book.aheadhealth.com (membership-first) returns 204
// with that origin allowed (checked 2026-10-15).
const POSTHOG_INGEST_HOST = "https://my.aheadhealth.com/ingest";

/**
 * The options for `<PostHogProvider>`. Reads the cookies when called, so call
 * it once, at start-up.
 *
 * `flow` names the app on every event it sends, autocaptured `$pageview`s
 * included. Set in `before_send` rather than with `posthog.register`, which
 * does nothing before init and lives in the consent-dependent persistence.
 * The booking app passes none: events without `flow` are the booking flow.
 *
 * `onLoaded` runs once PostHog has started, after the consent is applied:
 * the provider starts it in an effect, after the first screen has mounted,
 * so what that screen captured waits for it.
 *
 * Email links carry `?ph=<person hash>` (email-flows); the visitor is
 * identified with it here, once PostHog has started. A component's mount
 * effect (the former PersonHashIdentify) runs before the provider's init
 * effect, where `identify` is dropped as uninitialised.
 *
 * `pinExperimentVariants` keeps the variants the edge bucketed this visitor
 * into (the booking-flow cookies) as PostHog's own for the page. Identifying
 * the visitor makes PostHog load the flags again for the person, whose
 * variant can be another: from then on `$feature/<flag>` on every event,
 * and the exposure, named a variant the visitor was never shown, while the
 * page went on showing the cookie's (AHD-14337: a membership-first visitor
 * came in from an email link and switched arms mid-flow).
 */
export const buildPosthogOptions = ({
  flow,
  onLoaded,
  pinExperimentVariants = false,
}: {
  flow?: string;
  onLoaded?: () => void;
  pinExperimentVariants?: boolean;
} = {}): Partial<PostHogConfig> => {
  // Bootstrap all booking-flow experiment variants from cookies set by middleware.ts.
  // Cookie format: booking-flow-<flagKey>=<variant>. Generic — no per-experiment code needed.
  const COOKIE_PREFIX = "booking-flow-";
  const bookingFlowFlags: Record<string, string> = {};
  // Prefer the cross-domain shared identity (ahead-experiment-id, set by the
  // marketing-site proxy on the .aheadhealth.com apex) so a marketing-site
  // experiment exposure stitches to the booking conversion. Fall back to the
  // booking-local bes_did for direct-to-booking visitors. Must match the id
  // middleware.ts bucketed with.
  const sharedDistinctId = (() => {
    const shared = document.cookie.match(/(?:^|; )ahead-experiment-id=([^;]*)/);
    if (shared) return decodeURIComponent(shared[1]);
    const bes = document.cookie.match(/(?:^|; )bes_did=([^;]*)/);
    return bes ? decodeURIComponent(bes[1]) : undefined;
  })();
  for (const part of document.cookie.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    const name = part.slice(0, eq).trim();
    if (name.startsWith(COOKIE_PREFIX)) {
      bookingFlowFlags[name.slice(COOKIE_PREFIX.length)] = decodeURIComponent(
        part.slice(eq + 1).trim(),
      );
    }
  }

  const inTestMode = isTestMode();

  const options: Partial<PostHogConfig> = {
    // Ingestion goes to Ahead's own endpoint, see POSTHOG_INGEST_HOST above.
    // `ui_host` stays on the real PostHog UI so the toolbar and session-replay
    // deep links keep resolving.
    api_host: POSTHOG_INGEST_HOST,
    ui_host: "https://eu.posthog.com",
    defaults: "2025-11-30" as const,
    // Share the anonymous distinct_id with www.aheadhealth.com across the
    // .aheadhealth.com apex, so a PostHog experiment exposure on the marketing
    // site stitches to the booking conversion that follows it. Without this the
    // booking origin mints its own host-scoped distinct_id and every marketing
    // funnel/conversion experiment metric reads ~0 (verified: 0/243 of booking
    // converters shared a person with the exposed set). Mirrors
    // marketing-site/lib/posthog.ts. Host-scoped on preview domains
    // (*.vercel.app) where there's no shared apex.
    cross_subdomain_cookie:
      window.location.hostname.endsWith(".aheadhealth.com"),
    // Consent-aware init: a fresh EU visitor (or one who revoked analytics on any
    // .aheadhealth.com subdomain, mirrored via the shared cc_cookie) starts in
    // `memory` persistence AND opted out of capture — so cold-loading booking
    // after revoking on marketing does NOT capture (AHD-7751 / AHD-7210). The
    // <InitialCookieConsent> banner's syncConsent flips capture on when consent
    // is granted. Pre-consented visitors (test mode, ?acceptCookies=1, Swiss geo,
    // prior cc_cookie analytics grant) keep localStorage+cookie and capture.
    persistence: consentAwarePosthogPersistence(),
    loaded: (ph: PostHog) => {
      applyInitialPosthogConsent(ph);
      if (pinExperimentVariants && Object.keys(bookingFlowFlags).length > 0) {
        ph.featureFlags.overrideFeatureFlags({
          flags: bookingFlowFlags,
          suppressWarning: true,
        });
      }
      identifyFromUrlPh(ph);
      onLoaded?.();
    },
    // Scrub all on-page text from session replays so the booking flow never
    // leaks PII (AHD-7781). Mirrors report-app's masking. Test mode stays
    // unmasked so browser tests can still read the page.
    mask_all_text: !inTestMode,
    session_recording: inTestMode
      ? undefined
      : { maskAllInputs: true, maskTextSelector: "*", blockSelector: "img" },
    // Adopt the shared apex identity whenever it's present — independent of any
    // booking-flow experiment being live. This is what makes a marketing-site
    // exposure stitch to the booking conversion. Gating it on bookingFlowFlags
    // (as an earlier revision did) would tie cross-domain stitching to a booking
    // experiment happening to be running; archiving that experiment would then
    // silently re-zero every marketing→booking funnel metric. featureFlags are
    // only bootstrapped when a booking experiment is actually live.
    ...(sharedDistinctId
      ? {
          bootstrap: {
            distinctID: sharedDistinctId,
            ...(Object.keys(bookingFlowFlags).length > 0 && {
              featureFlags: bookingFlowFlags,
            }),
          },
        }
      : {}),
  };

  if (flow) {
    options.before_send = (event) => {
      if (event) event.properties = { flow, ...event.properties };
      return event;
    };
  }
  return options;
};
